import { IKnmHistoryItem } from 'APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { apiDelete, apiGet, apiPost, apiPut } from 'APIPackage/MainModule/common/apiTools';
import { getServiceBaseURL } from 'APIPackage/MainModule/common/getServiceBaseURL';
import { APIServiceEnum } from 'APIPackage/MainModule/enums/APIServiceEnum';
import { ISign } from 'APIPackage/KnmModule/interfaces/ISign';
import { ISignSubjectInfo, ISignSubjectInfoCheckResult } from 'APIPackage/KnmModule/interfaces/ISignSubjectInfo';
import { IErknmPrescribeCancel } from 'APIPackage/KnmModule/interfaces/IErknmPrescribeCancel';
import { IErknmResultCancel } from 'APIPackage/KnmModule/interfaces/IErknmResultCancel';
import { KnmDraftTypesEnum } from 'APIPackage/KnmModule/enums/KnmDraftTypesEnum';
import { IKnmViolatedRequirementsNpa248 } from 'APIPackage/KnmModule/interfaces/IKnmViolatedRequirementsNpa248';

import { IDecisionParams } from '../interfaces/IDecisionParams';
import { IDecision } from '../interfaces/IDecision';

export function fetchDecisionById(id: number): Promise<IDecision> {
  return apiGet(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}`);
}

export function fetchDecisionCreate(body: IDecisionParams): Promise<IDecision> {
  return apiPost({ url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport`, body });
}

export function fetchDecisionUpdate(body: IDecisionParams): Promise<IDecision> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport`, body);
}

export async function fetchDecisionDraft(id: number): Promise<IDecision> {
  return apiGet<IDecision>(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/drafts/${KnmDraftTypesEnum.DECISION_PASSPORT}/${id}`,
  );
}

export function fetchDecisionDraftSave(body: IDecisionParams): Promise<IDecision> {
  return apiPut(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/drafts/${KnmDraftTypesEnum.DECISION_PASSPORT}/${body.id}`,
    body,
  );
}

export async function fetchDecisionDraftDelete(id: number): Promise<void> {
  return apiDelete(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/drafts/${KnmDraftTypesEnum.DECISION_PASSPORT}/${id}`,
  );
}

export function fetchDecisionDelete(id: number): Promise<void> {
  return apiDelete(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}`);
}

export function fetchDecisionRestore(id: number): Promise<IDecision> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/restore`);
}

export async function fetchDecisionHistory(id: number): Promise<IKnmHistoryItem[]> {
  return apiGet(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/history`);
}

export async function fetchDecisionActCancel(data: IErknmResultCancel): Promise<number> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/results/cancel`, data);
}

export async function fetchDecisionPrescribeCancel(data: IErknmPrescribeCancel): Promise<number> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/prescription/cancel`, data);
}

export async function decisionCheckSignCertificate(
  subjectInfo: ISignSubjectInfo,
  id?: number,
): Promise<ISignSubjectInfoCheckResult> {
  return apiPost<ISignSubjectInfoCheckResult>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/check-certificate-fields`,
    body: subjectInfo,
  });
}

export async function fetchDecisionSignedHistory(
  id: number,
  startDateTime: string,
  endDateTime: string,
): Promise<IKnmHistoryItem[]> {
  const urlSearchParams = new URLSearchParams();

  urlSearchParams.append('startDateTime', startDateTime);
  urlSearchParams.append('endDateTime', endDateTime);

  return apiGet(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/changes-for-sign?${urlSearchParams}`,
  );
}

export async function fetchDecisionSignUpload(file: File, id: number): Promise<boolean> {
  const formData = new FormData();

  formData.append('file', file);

  return apiPost<boolean>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/signature-upload`,
    body: formData,
  });
}

export async function fetchDecisionXmlDownloadById(id: number): Promise<string> {
  return apiGet<string>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/xml`);
}

export async function deleteDecisionSignById(id: number): Promise<void> {
  return apiDelete(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/signature`);
}

export async function fetchDecisionSignById(data: ISign): Promise<void> {
  const { id, document, file, signature, fileName, actGuid, prescriptionGuid, isCancelSign } = data;
  const formData = new FormData();

  formData.append('file', file);
  formData.append('document', document);
  formData.append('signature', signature);
  formData.append('fileName', fileName);
  if (actGuid) formData.append('actGuid', actGuid);
  if (prescriptionGuid) formData.append('prescriptionGuid', prescriptionGuid);
  if (isCancelSign) formData.append('isCancelSign', String(isCancelSign));

  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/signature`,
    body: formData,
  });
}

export async function fetchDecisionRestoreLatestSignedVersion(id: number | null): Promise<IDecision> {
  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/restore-to-latest-signed`,
    body: {},
  });
}

export async function fetchIsDecisionCanBeSigned(id: number): Promise<number> {
  return apiGet<number>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/is-can-be-signed`);
}

export async function fetchExcludedViolationsFromReinspection(id: number): Promise<IKnmViolatedRequirementsNpa248[]> {
  return apiGet<IKnmViolatedRequirementsNpa248[]>(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${id}/excluded-violations-from-reinspection`,
  );
}

export async function fetchTestDecisionSignById(decisionId: number): Promise<void> {
  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/${decisionId}/test-signature`,
  });
}

export async function updateDecisionControlObjects(passportId: number, cosIds: string[]) {
  return apiPost<boolean>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/decision-passport/update-control-objects`,
    body: { cosIds, id: passportId },
  });
}
